'use client';

import { useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import {
  type DynamicTranslator,
  pickApiCode,
  pickUserMessageKey,
  resolveApiErrorMessage,
} from '@/lib/errors/resolve-api-error';
import { reportError, trackAction } from '@/lib/telemetry';
import { showToast } from '@/lib/ui/toast';

interface MutationMeta {
  readonly skipGlobalErrorHandler?: boolean;
}

/**
 * Global mutation-error funnel.
 *
 * Subscribes to TanStack Query's mutation cache and, for every mutation that
 * settles in `error` state, resolves a user-facing message via the shared
 * fallback ladder in `@/lib/errors/resolve-api-error`, then:
 *   1. shows a single toast keyed on `TOAST_IDS.TX_FAILED` (Sonner replaces
 *      a same-id toast, so back-to-back mutation errors don't stack), and
 *   2. fans out telemetry: every error to `trackAction('mutation_error')`,
 *      and errors without an API code to Better Stack Errors (`reportError`).
 *
 * Opt-out per mutation: pass `meta: { skipGlobalErrorHandler: true }` in the
 * mutation options. Local toast/state callsites that render their own copy
 * should set this so the global funnel doesn't double-fire.
 *
 * Mounted inside the locale tree (under `LocaleProvider`) so
 * `useTranslations` is in scope. `useQueryClient` resolves to the SDK's
 * `QueryClient` (mounted by `SDKProvider` above the locale tree), which is
 * also where every SDK mutation fires — the subscription sees them all.
 */
export function MutationErrorListener(): null {
  const queryClient = useQueryClient();
  // Root translator (no namespace) so the resolver can dispatch full dotted
  // keys across BOTH the `errors.*` (REST/SDK) and `tx.errors.*` (on-chain)
  // namespaces, and use `t.has` to probe key existence.
  // ALLOW-UNKNOWN: next-intl `t` is typed against the static message tree; the resolver dispatches dynamic SDK-emitted keys, so we narrow the function shape (not the value).
  const t = useTranslations() as unknown as DynamicTranslator;

  useEffect(() => {
    const cache = queryClient.getMutationCache();
    return cache.subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.action.type !== 'error') return;

      const meta = event.mutation.options.meta as MutationMeta | undefined;
      if (meta?.skipGlobalErrorHandler === true) return;

      const error = event.action.error;
      const message = resolveApiErrorMessage(error, t);
      // `null` ⇒ user-cancelled (`tx.errors.cancelled`): no toast, no telemetry.
      if (message === null) return;

      const apiCode = pickApiCode(error);
      const userMessageKey = pickUserMessageKey(error, t);

      showToast({
        id: TOAST_IDS.TX_FAILED,
        message,
        variant: 'error',
      });

      // Preserve the original error's message field when coercing — a
      // thrown plain object `{ message, code, ... }` would otherwise
      // stringify as `[object Object]` and lose the actual signal.
      let errInstance: Error;
      if (error instanceof Error) {
        errInstance = error;
      } else if (
        error !== null &&
        typeof error === 'object' &&
        typeof (error as { message?: string }).message === 'string'
      ) {
        errInstance = new Error((error as { message: string }).message);
      } else {
        errInstance = new Error(String(error));
      }
      // A coded error is an expected, classified outcome (validation, auth,
      // on-chain rejection) the backend already logs; the Errors app gets only
      // the ones nothing classified, and `mutation_error` below counts all.
      if (apiCode === undefined) {
        reportError(errInstance, { source: 'mutation-error-listener', userMessageKey });
      }
      trackAction('mutation_error', {
        user_message_key: userMessageKey,
        name: errInstance.name,
        ...(apiCode !== undefined ? { api_code: apiCode } : {}),
      });
    });
  }, [queryClient, t]);

  return null;
}
